import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import * as THREE from 'three';
function createCobblestoneTexture(size = 1024) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    // Светлые швы
    ctx.fillStyle = '#8a8278';
    ctx.fillRect(0, 0, size, size);
    const cols = 12;
    const rows = 10;
    const cellW = size / cols;
    const cellH = size / rows;
    const rng = (min, max) => min + Math.random() * (max - min);
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            const offsetX = (row % 2 === 0) ? 0 : cellW * 0.5;
            const cx = col * cellW + offsetX + cellW / 2 + rng(-cellW * 0.08, cellW * 0.08);
            const cy = row * cellH + cellH / 2 + rng(-cellH * 0.08, cellH * 0.08);
            const w = cellW * rng(0.72, 0.84);
            const h = cellH * rng(0.72, 0.84);
            const radius = Math.min(w, h) * 0.22;
            // Тёплые светлые серо-бежевые тона
            const palettes = [
                [185, 178, 168],
                [172, 165, 155],
                [195, 188, 178],
                [160, 155, 148],
                [178, 170, 160],
                [190, 182, 170],
            ];
            const [br, bg, bb] = palettes[Math.floor(Math.random() * palettes.length)];
            const vr = rng(-12, 12);
            ctx.beginPath();
            ctx.moveTo(cx - w / 2 + radius, cy - h / 2);
            ctx.lineTo(cx + w / 2 - radius, cy - h / 2);
            ctx.arcTo(cx + w / 2, cy - h / 2, cx + w / 2, cy - h / 2 + radius, radius);
            ctx.lineTo(cx + w / 2, cy + h / 2 - radius);
            ctx.arcTo(cx + w / 2, cy + h / 2, cx + w / 2 - radius, cy + h / 2, radius);
            ctx.lineTo(cx - w / 2 + radius, cy + h / 2);
            ctx.arcTo(cx - w / 2, cy + h / 2, cx - w / 2, cy + h / 2 - radius, radius);
            ctx.lineTo(cx - w / 2, cy - h / 2 + radius);
            ctx.arcTo(cx - w / 2, cy - h / 2, cx - w / 2 + radius, cy - h / 2, radius);
            ctx.closePath();
            // Линейный градиент — свет сверху-слева
            const grad = ctx.createLinearGradient(cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2);
            grad.addColorStop(0, `rgb(${br + vr + 22}, ${bg + vr + 18}, ${bb + vr + 14})`);
            grad.addColorStop(0.4, `rgb(${br + vr + 6},  ${bg + vr + 4},  ${bb + vr + 2})`);
            grad.addColorStop(1, `rgb(${br + vr - 18}, ${bg + vr - 16}, ${bb + vr - 14})`);
            ctx.fillStyle = grad;
            ctx.fill();
            ctx.strokeStyle = 'rgba(255,255,255,0.2)';
            ctx.lineWidth = 0.8;
            ctx.stroke();
        }
    }
    // Зернистость
    for (let i = 0; i < 8000; i++) {
        const a = Math.random() * 0.04;
        ctx.fillStyle = Math.random() > 0.5 ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${a})`;
        ctx.fillRect(Math.random() * size, Math.random() * size, 1, 1);
    }
    return canvas;
}
function createNormalMap(size = 1024) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#8080ff';
    ctx.fillRect(0, 0, size, size);
    const cols = 12;
    const rows = 10;
    const cellW = size / cols;
    const cellH = size / rows;
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            const offsetX = (row % 2 === 0) ? 0 : cellW * 0.5;
            const cx = col * cellW + offsetX + cellW / 2;
            const cy = row * cellH + cellH / 2;
            const w = cellW * 0.78;
            const h = cellH * 0.78;
            const grad = ctx.createRadialGradient(cx - w * 0.1, cy - h * 0.1, 0, cx, cy, Math.max(w, h) * 0.6);
            grad.addColorStop(0, '#9898ff');
            grad.addColorStop(0.5, '#8080ff');
            grad.addColorStop(1, '#6468ee');
            ctx.fillStyle = grad;
            ctx.beginPath();
            ctx.ellipse(cx, cy, w / 2, h / 2, 0, 0, Math.PI * 2);
            ctx.fill();
        }
    }
    return canvas;
}
export function Ground() {
    const position = [0, -0.0001, 0];
    const rotation = [-Math.PI / 2, 0, 0];
    const args = [30, 30];
    const [colorMap, normalMap] = useMemo(() => {
        const color = new THREE.CanvasTexture(createCobblestoneTexture(1024));
        color.wrapS = color.wrapT = THREE.RepeatWrapping;
        color.repeat.set(10, 10);
        color.anisotropy = 16;
        const normal = new THREE.CanvasTexture(createNormalMap(1024));
        normal.wrapS = normal.wrapT = THREE.RepeatWrapping;
        normal.repeat.set(10, 10);
        return [color, normal];
    }, []);
    return (_jsxs("mesh", { rotation: rotation, position: position, receiveShadow: true, children: [_jsx("planeGeometry", { args: args }), _jsx("meshStandardMaterial", { map: colorMap, normalMap: normalMap, normalScale: new THREE.Vector2(0.8, 0.8), roughness: 0.9, metalness: 0.0 })] }));
}
